import React, { useEffect, useState, useRef } from "react"
import { submitAnswers, endWriting, endGame } from "./scattergoriesRoom"
import { LISTS } from "../../../data/scattergoriesLists"
import { Screen, SgButton, Pill, PlayerDot, C, F } from "./ScattergoriesUI"
import { playSound } from "../../../utils/ballparkSounds"
import { matchesLetter } from "../../../utils/scattergoriesLogic"

const WritingScreen = ({ room, code, playerId, isHost, setShowScores }) => {
  const round = room.round || 1
  const letter = room.letter || "A"
  const listId = room.listId || 1
  const duration = room.duration || 120
  
  const listData = LISTS.find((l) => l.id === listId) || LISTS[0]
  const categories = listData.categories

  // Local answers state (array of 12 strings), restoring existing answers if reconnecting
  const [answers, setAnswers] = useState(() => {
    const existing = room.answers?.[round]?.[playerId]
    return existing && Array.isArray(existing) && existing.length === 12
      ? [...existing]
      : Array(12).fill("")
  })

  const [focusedInputIdx, setFocusedInputIdx] = useState(null)

  // Timer state
  const [timeLeft, setTimeLeft] = useState(() =>
    Math.max(0, Math.ceil(((room.writingEndAt || Date.now()) - Date.now()) / 1000))
  )

  const players = Object.entries(room.players)
  const roundAnswers = room.answers?.[round] || {}

  const answersRef = useRef(answers)
  answersRef.current = answers

  // 1. Debounced background auto-save as player types
  useEffect(() => {
    const hasContent = answers.some((a) => a.trim() !== "")
    if (!hasContent) return

    const delay = setTimeout(() => {
      submitAnswers(code, round, playerId, answers)
    }, 1000)

    return () => clearTimeout(delay)
  }, [answers, code, round, playerId])

  // 2. Countdown timer hook & host transition control
  useEffect(() => {
    const checkTimer = () => {
      const remaining = Math.max(
        0,
        Math.ceil(((room.writingEndAt || Date.now()) - Date.now()) / 1000)
      )
      setTimeLeft(remaining)

      if (remaining <= 0) {
        // Immediate final save when time runs out
        submitAnswers(code, round, playerId, answersRef.current)

        // Host advances the phase to scoring
        if (isHost) {
          endWriting(code)
        }
      }
    }

    checkTimer()
    const timer = setInterval(checkTimer, 500)
    return () => clearInterval(timer)
  }, [room.writingEndAt, isHost, code, round, playerId])

  const alarmPlayedRef = useRef(false)

  // Reset alarm ref when round changes
  useEffect(() => {
    alarmPlayedRef.current = false
  }, [round])

  // Play warning alarm at 10 seconds remaining
  useEffect(() => {
    if (timeLeft <= 10 && timeLeft > 0 && !alarmPlayedRef.current) {
      alarmPlayedRef.current = true
      playSound("alarm")
    }
  }, [timeLeft])

  const handleTextChange = (idx, text) => {
    setAnswers((prev) => {
      const next = [...prev]
      next[idx] = text
      return next
    })
  }

  // Helper to count non-empty answers for any player
  const getAnswerCount = (pid) => {
    const pAns = roundAnswers[pid] || []
    return pAns.filter((a) => a.trim() !== "").length
  }

  // Determine timer ring color
  let timerColor = C.green
  if (timeLeft <= 15) timerColor = C.amber
  if (timeLeft <= 5) timerColor = C.coral

  return (
    <Screen style={{ gap: 10, maxHeight: "100dvh", overflowY: "auto", padding: "12px 12px 16px" }}>
      {/* Compact Top Header & Timer Row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: C.card,
          border: `1.5px solid ${C.border}`,
          borderRadius: 16,
          padding: "8px 12px",
          boxShadow: `0 3px 0 ${C.border}`,
          flexShrink: 0,
        }}
      >
        {/* Left Section: Letter and Timer */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Letter */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase" }}>Letter</span>
            <span
              style={{
                fontFamily: F.display,
                fontWeight: 900,
                fontSize: 22,
                color: C.indigo,
                lineHeight: 1,
              }}
            >
              {letter}
            </span>
          </div>

          {/* Divider */}
          <div style={{ width: 1.5, height: 18, background: C.border }} />

          {/* Circular/Compact Timer */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
            <span
              style={{
                fontFamily: F.display,
                fontWeight: 900,
                fontSize: 20,
                color: timerColor,
                lineHeight: 1,
              }}
            >
              {timeLeft}
            </span>
            <span style={{ fontSize: 9, fontWeight: 800, color: C.muted, textTransform: "uppercase" }}>sec</span>
          </div>
        </div>

        {/* Right Section: Game Details & Scores Button */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.muted, letterSpacing: 0.5 }}>
              {listData.name.toUpperCase()}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.ink, marginTop: 1 }}>
              Room {code} · Rd {round}
            </div>
          </div>

          <button
            onClick={() => {
              playSound("click")
              setShowScores(true)
            }}
            style={{
              background: C.panel,
              border: `1.5px solid ${C.borderDark}`,
              borderRadius: 10,
              padding: "4px 8px",
              fontFamily: F.display,
              fontWeight: 800,
              fontSize: 11,
              color: C.ink,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            🏆 <span style={{ fontSize: 10 }}>Scores</span>
          </button>
        </div>
      </div>

      {/* 12 Categories Input List */}
      <div
        style={{
          flex: 1,
          // Same collapse guard as ScoringScreen: the host's extra button row
          // would otherwise shrink this scroll pane toward zero on small phones.
          minHeight: 160,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          paddingRight: 4,
          margin: "4px 0",
        }}
      >
        {categories.map((cat, idx) => {
          const ans = answers[idx]
          const hasText = ans.trim() !== ""
          const isWrongLetter = hasText && !matchesLetter(ans, letter)

          return (
            <div
              key={idx}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                background: C.card,
                border: `1.5px solid ${C.border}`,
                borderRadius: 12,
                padding: "10px 12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
              }}
            >
              {/* Category Label (Top Side) */}
              <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <span
                  style={{
                    fontFamily: F.display,
                    fontWeight: 900,
                    color: C.indigo,
                    fontSize: 14,
                    marginTop: 1,
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}.
                </span>
                <span style={{ fontWeight: 800, fontSize: 14, color: C.ink, lineHeight: 1.25 }}>
                  {cat}
                </span>
              </div>

              {/* Input Container (Bottom Side) */}
              <div style={{ display: "flex", alignItems: "center", position: "relative", width: "100%" }}>
                <input
                  style={{
                    height: 38,
                    borderRadius: 8,
                    border: isWrongLetter
                      ? `1.5px solid ${C.coral}`
                      : focusedInputIdx === idx
                      ? `1.5px solid ${C.indigo}`
                      : `1.2px solid ${C.borderDark}`,
                    background: C.white,
                    padding: "0 12px",
                    fontSize: 16, // prevents iOS zoom-on-focus behavior
                    fontWeight: 700,
                    fontFamily: F.body,
                    color: isWrongLetter ? C.coral : C.ink,
                    width: "100%",
                    boxSizing: "border-box",
                    outline: "none",
                    boxShadow: isWrongLetter
                      ? `0 0 0 3px ${C.coral}15`
                      : focusedInputIdx === idx
                      ? `0 0 0 3px ${C.indigo}15`
                      : "none",
                    transition: "all 0.15s ease",
                  }}
                  placeholder={`Starts with "${letter}"...`}
                  value={answers[idx]}
                  onChange={(e) => handleTextChange(idx, e.target.value)}
                  onFocus={() => setFocusedInputIdx(idx)}
                  onBlur={() => setFocusedInputIdx(null)}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer Info and Actions */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        {/* Dynamic Player Progress Grid */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            fontSize: 11,
            fontWeight: 800,
            color: C.muted,
            flexWrap: "wrap",
            background: C.panel,
            padding: "4px 8px",
            borderRadius: 10,
            border: `1.5px solid ${C.border}`,
          }}
        >
          {players.map(([pid, p]) => {
            const count = getAnswerCount(pid)
            // If current player, count local answers instead of Firestore wait
            const displayCount = pid === playerId ? answers.filter(a => a.trim() !== "").length : count
            return (
              <span
                key={pid}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: C.card,
                  borderRadius: 6,
                  padding: "2px 6px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                }}
              >
                <PlayerDot color={p.color} size={8} />
                <span style={{ color: C.ink }}>
                  {p.name} ({displayCount}/12)
                </span>
              </span>
            )
          })}
        </div>

        {isHost && (
          <div style={{ display: "flex", gap: 8 }}>
            <SgButton
              variant="coral"
              style={{ height: 38, fontSize: 13, flex: 1 }}
              onClick={() => {
                playSound("click")
                // Final save for host just in case, then transition
                submitAnswers(code, round, playerId, answersRef.current)
                endWriting(code)
              }}
            >
              Force End Round
            </SgButton>
            <SgButton
              variant="ghost"
              style={{ height: 38, fontSize: 12, flex: 1 }}
              onClick={() => {
                playSound("click")
                endGame(code)
              }}
            >
              End Game
            </SgButton>
          </div>
        )}
      </div>
    </Screen>
  )
}

export default WritingScreen
